import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Row, Col } from 'antd';
import ErrorBoundary from '../../../components/Error/ErrorBoundary';
import Card from '../../../components/Card/Card';
import DashboardTeamAction from './DashboardTeamAction';
import Progress from '../../../components/widget/Progress';
import { BusinessLocation, HazardIndex, RaChart, BiaChart, BiaDetail, RtoRpoChart } from '../../../components/widget';
import { fetchBranch } from '../../Organisation/scenes/Branch/BranchAction';
import { fetchAlternativeSite } from '../../Organisation/scenes/AlternativeSite/AlternativeSiteAction';
import { fetchCustomer } from '../../Organisation/scenes/Customer/CustomerAction';
import { fetchSupplier } from '../../Organisation/scenes/Supplier/SupplierAction';
import { fetchStrategicThirdParty } from '../../Organisation/scenes/StrategicThirdParty/StrategicThirdPartyAction';
import { fetchPlanProgress, fetchPlanSubscriptions } from '../scenes/Plan/PlanAction';
import { fetchStrategicBia, fetchTacticalBia, fetchDrsCategories } from '../scenes/Bia/BiaAction';
import {
  fetchRisks,
  fetchRiskCategories,
  fetchRiskLikelihoodPresets,
  fetchRiskReport,
} from '../scenes/Risk/RiskAction';
import { fetchHazardIndex, storeGeoCode } from '../../Organisation/OrganisationAction';
import { planSelector, planIdSelector } from '../scenes/Plan/PlanSelectors';
import {
  geoCodeSelector,
  orgIdSelector,
  industryCodeSelector,
  hazardIndexSelector,
  industryNameSelector,
  dashboardStageLockingSelector,
  teamPlanProgressiveUnlockSelector,
  dashboardContentVariantSelector,
  dashboardCardMuteDisabledPanelsSelector,
} from '../../Organisation/OrganisationSelectors';
import { alternativeSiteListSelector } from '../../Organisation/scenes/AlternativeSite/AlternativeSiteSelectors';
import { branchesSelector } from '../../Organisation/scenes/Branch/BranchSelectors';
import { customerListSelector } from '../../Organisation/scenes/Customer/CustomerSelectors';
import { supplierListSelector } from '../../Organisation/scenes/Supplier/SupplierSelectors';
import { strategicThirdPartyListSelector } from '../../Organisation/scenes/StrategicThirdParty/StrategicThirdPartySelectors';
import {
  risksSelector,
  riskCategoriesSelector,
  riskLikelihoodPresetsSelector,
  riskReportSelector,
} from '../scenes/Risk/RiskSelectors';
import {
  strategicBiasSelector,
  drsCategoriesSelector,
  drsHasDataSelector,
  tacticalBiasSelector,
} from '../scenes/Bia/BiaSelectors';
import { buildMutedState } from '../../../utils/mute';

class DashboardMainPanel extends Component {
  state = { showBiaDetail: false };

  componentDidMount() {
    this.loadData();
  }

  loadData = () => {
    const { orgId, industryCode, planId } = this.props;
    /* eslint react/destructuring-assignment: off */
    this.props.fetchBranch(orgId);
    this.props.fetchPlanProgress();
    this.props.fetchRiskCategories();
    this.props.fetchRiskLikelihoodPresets();
    this.props.fetchRisks(planId);
    this.props.fetchRiskReport(planId);
    this.props.fetchCustomer(orgId);
    this.props.fetchSupplier(orgId);
    this.props.fetchAlternativeSite(orgId);
    this.props.fetchStrategicBia(planId);
    this.props.fetchTacticalBia(planId);
    this.props.fetchDrsCategories();
    this.props.fetchHazardIndex(industryCode);
    this.props.fetchStrategicThirdParty(orgId);
    this.props.fetchPlanSubscriptions(planId);
  };

  toggleBia = () => {
    this.setState(({ showBiaDetail }) => ({ showBiaDetail: !showBiaDetail }));
  };

  render() {
    const {
      geoCode = {},
      industryName,
      branches,
      hazardIndex,
      currentPlan,
      risks,
      riskCategories,
      riskLikelihoodPresets = [],
      customerList,
      supplierList,
      alternativeSiteList,
      strategicBias,
      tacticalBias,
      drsCategories,
      drsHasData,
      riskReport,
      isDashboardStageLockingEnabled,
      isTeamPlanProgressiveUnlockEnabled,
      dashboardContentVariant,
      dashboardCardMuteDisabledPanels = [],
    } = this.props;
    const { showBiaDetail } = this.state;
    const progress = currentPlan?.progress || {};
    const hasRiskReport = Boolean(riskReport);
    const buildCardMuted = ({ panelKey, isLocked }) =>
      buildMutedState({
        panelKey,
        isLocked,
        isFeatureEnabled: isDashboardStageLockingEnabled,
        mutedDisabledPanels: dashboardCardMuteDisabledPanels,
      });

    const isStageComplete = (value) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return false;
      if (numericValue <= 1) return numericValue >= 0.999;
      return numericValue >= 99.9;
    };

    const isPolicyProgramComplete = isStageComplete(progress.policy_programme);
    const isRiskAnalysisComplete = isStageComplete(progress.risk_analysis);
    const isAnalysisPlanningComplete = isStageComplete(progress.analysis_planning);
    const isPlanningDesignComplete = isStageComplete(progress.planning_design);

    const isProgressCardMuted = buildCardMuted({
      panelKey: 'progress',
      // isLocked: !isPolicyProgramComplete || !isAnalysisPlanningComplete || !isPlanningDesignComplete,
      isLocked: false,
    });
    const isBusinessLocationsCardMuted = buildCardMuted({
      panelKey: 'businessLocations',
      // isLocked: !isPolicyProgramComplete,
      isLocked: false,
    });
    const isHazardIndexCardMuted = buildCardMuted({
      panelKey: 'hazardIndex',
      // isLocked: !isRiskAnalysisComplete,
      isLocked: false,
    });
    const isRiskAnalysisCardMuted = buildCardMuted({
      panelKey: 'riskAnalysis',
      isLocked: !isRiskAnalysisComplete,
    });
    const isBusinessImpactAnalysisCardMuted = buildCardMuted({
      panelKey: 'businessImpactAnalysis',
      isLocked: !isAnalysisPlanningComplete,
    });
    const isRtoRpoCardMuted = buildCardMuted({
      panelKey: 'rtoRpo',
      isLocked: !isPlanningDesignComplete || !hasRiskReport,
    });
    const teamPlanAccessByKey = isTeamPlanProgressiveUnlockEnabled
      ? {
          ERT: isPolicyProgramComplete,
          CMT: isAnalysisPlanningComplete,
          IT: isPlanningDesignComplete,
        }
      : {
          ERT: true,
          CMT: true,
          IT: true,
        };       

    return (
      <div className="dashboard-container">
        <DashboardTeamAction
          teamPlanAccessByKey={teamPlanAccessByKey}
          variant={dashboardContentVariant}
        />
        {showBiaDetail ? (
          <BiaDetail toggleBia={this.toggleBia} />
        ) : (
          <Row type="flex" gutter={20}>
            <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>
              <Card
                color="#F1C400"
                title="Progress"
                description="The following bar graph will display your progress through the different sections of your plan. You will not be able to Implement your plan until Policy & Program, Analysis & Planning and Planning & Design each reach 100%."
                muted={isProgressCardMuted}
                mutedLabel="Program setup incomplete"
              >
                <ErrorBoundary>
                  <Progress progress={currentPlan.progress} variant={dashboardContentVariant} />
                </ErrorBoundary>
              </Card>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>
              <Card
                color="#343579"
                title="Business Locations"
                description="The Map below plots your Branches, Strategic Customers and Suppliers and your Short Term Recovery Sites. Additional pins can be added from the Organisation area."
                muted={isBusinessLocationsCardMuted}
                mutedLabel="Policy & Program incomplete"
              >
                <ErrorBoundary>
                  <BusinessLocation
                    branches={branches}
                    customerList={customerList}
                    supplierList={supplierList}
                    alternativeSiteList={alternativeSiteList}
                    geoCode={geoCode}
                    storeGeoCode={storeGeoCode}
                  />
                </ErrorBoundary>
              </Card>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>
              <Card
                color="#CB2C30"
                title="Hazard Index"
                description="The following chart is a graphical representation or the likelihood and severity of a loss occurring within any of the 13 classes of insurance listed in the chart."
                muted={isHazardIndexCardMuted}
                mutedLabel="Risk Analysis incomplete"
              >
                <ErrorBoundary>
                  <HazardIndex industryName={industryName} data={hazardIndex} />
                </ErrorBoundary>
              </Card>
            </Col>
            {risks && Object.entries(risks).length > 0 && riskCategories && riskLikelihoodPresets.length > 0 && (
              <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>
                <Card
                  color="#00685E"
                  title="Risk Analysis"
                  description="The following bubble chart provides a graphical representation of your organisation's monetary exposure to an adverse impact against time. The graph shows both your total and annualized monetary exposure. Changes to this chart can be made from the Risk and Threat Assessment sections."
                  muted={isRiskAnalysisCardMuted}
                  mutedLabel="Risk Analysis incomplete"
                >
                  <ErrorBoundary>
                    <RaChart
                      risks={risks}
                      riskCategories={riskCategories}
                      riskLikelihoodPresets={riskLikelihoodPresets}
                    />
                  </ErrorBoundary>
                </Card>
              </Col>
            )}
            {strategicBias && drsCategories && drsHasData && (
              <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>
                <Card
                  color="#343579"
                  title="Business Impact Analysis"
                  description="The following chart identifies the scale of severity of a potential impact to the Strategic Areas of your business that you have identified. Changes to the scales are made in the Strategic BIA area of the Business Impact Analysis."
                  onMore={this.toggleBia}
                  muted={isBusinessImpactAnalysisCardMuted}
                  mutedLabel="Analysis & Planning incomplete"
                >
                  <ErrorBoundary>
                    <BiaChart strategicBias={strategicBias} drsCategories={drsCategories} />
                  </ErrorBoundary>
                </Card>
              </Col>
            )}
            {tacticalBias && tacticalBias.length > 0 && currentPlan.mtpd && (
              <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>
                <Card
                  color="#343579"
                  title="Recovery Time Objective and Recovery Point Objective (RTO-RPO)"
                  description="The following chart shows your Recovery Time Objectives and Recovery Point Objectives set against your chosen Maximum Tolerable Period of Disruption. This information can be added/altered from the Tactical BIA (Business Impact Analysis) in the Analysis and Planning section."
                  muted={isRtoRpoCardMuted}
                  mutedLabel={
                    !isPlanningDesignComplete ? 'Planning & Design incomplete' : 'Risk & Insurance Report incomplete'
                  }
                >
                  <ErrorBoundary>
                    <RtoRpoChart tacticalBias={tacticalBias} mtpd={currentPlan.mtpd} />
                  </ErrorBoundary>
                </Card>
              </Col>
            )}
          </Row>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  orgId: orgIdSelector(state),
  geoCode: geoCodeSelector(state),
  branches: branchesSelector(state),
  industryName: industryNameSelector(state),
  industryCode: industryCodeSelector(state),
  hazardIndex: hazardIndexSelector(state),
  alternativeSiteList: alternativeSiteListSelector(state),
  customerList: customerListSelector(state),
  supplierList: supplierListSelector(state),
  strategicThirdPartyList: strategicThirdPartyListSelector(state),
  currentPlan: planSelector(state),
  planId: planIdSelector(state),
  risks: risksSelector(state),
  riskCategories: riskCategoriesSelector(state),
  riskLikelihoodPresets: riskLikelihoodPresetsSelector(state),
  riskReport: riskReportSelector(state),
  strategicBias: strategicBiasSelector(state),
  tacticalBias: tacticalBiasSelector(state),
  drsCategories: drsCategoriesSelector(state),
  drsHasData: drsHasDataSelector(state),
  isDashboardStageLockingEnabled: dashboardStageLockingSelector(state),
  isTeamPlanProgressiveUnlockEnabled: teamPlanProgressiveUnlockSelector(state),
  dashboardContentVariant: dashboardContentVariantSelector(state),
  dashboardCardMuteDisabledPanels: dashboardCardMuteDisabledPanelsSelector(state),
});

const mapDispatchToProps = {
  storeGeoCode,
  fetchBranch,
  fetchPlanProgress,
  fetchRisks,
  fetchRiskCategories,
  fetchRiskLikelihoodPresets,
  fetchRiskReport,
  fetchCustomer,
  fetchSupplier,
  fetchAlternativeSite,
  fetchStrategicBia,
  fetchTacticalBia,
  fetchDrsCategories,
  fetchHazardIndex,
  fetchStrategicThirdParty,
  fetchPlanSubscriptions,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardMainPanel);
